import React, { useEffect, useRef, useState, useCallback } from "react";
import { ResponsiveLine } from "@nivo/line";
import * as htmlToImage from "html-to-image";
import {
  Box,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
  Tooltip,
  Stack,
} from "@mui/material";
import { Download } from "lucide-react";
import dayjs from "dayjs";

/* ------------------------------- Interfaces ------------------------------ */
interface Test {
  diagnosisResults: "Positive" | "Negative" | "Indeterminate";
  timestamp: string;
}

interface ChartPoint {
  x: string;
  y: number;
}

interface LineChartProps {
  tests: Test[];
}

/* ----------------------------- Component --------------------------------- */
const LineChart: React.FC<LineChartProps> = ({ tests }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const chartRef = useRef<HTMLDivElement>(null);

  const [data, setData] = useState<{ id: string; data: ChartPoint[] }[]>([]);

  /* ---------- Aggregate LAST 12 MONTHS ---------- */
  const aggregateLastYear = useCallback(() => {
    const months = Array.from({ length: 12 }, (_, i) =>
      dayjs().subtract(11 - i, "month").format("MMM")
    );

    const totals: Record<string, number> = {};
    months.forEach((m) => (totals[m] = 0));

    tests
      .filter((t) => dayjs(t.timestamp).isValid())
      .filter((t) => dayjs(t.timestamp).isAfter(dayjs().subtract(1, "year")))
      .forEach((t) => {
        const month = dayjs(t.timestamp).format("MMM");
        if (totals[month] !== undefined) totals[month] += 1;
      });

    return [
      {
        id: "Total Tests",
        data: months.map((m) => ({ x: m, y: totals[m] })),
      },
    ];
  }, [tests]);

  useEffect(() => {
    setData(tests.length ? aggregateLastYear() : []);
  }, [tests, aggregateLastYear]);

  /* ---------- PNG Export ---------- */
  const handleExportPNG = useCallback(async () => {
    if (!chartRef.current) return;
    const dataUrl = await htmlToImage.toPng(chartRef.current, {
      quality: 0.95,
      backgroundColor: "#273142",
    });
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = "malaria_trends_last_12_months.png";
    link.click();
  }, []);

  /* ---------- Empty State ---------- */
  if (!tests.length) {
    return (
      <Box
        sx={{
          height: { xs: 240, sm: 320 },
          bgcolor: "#273142",
          borderRadius: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography color="rgba(255,255,255,0.6)">
          No test data available
        </Typography>
      </Box>
    );
  }

  /* ---------- Render ---------- */
  return (
    <Box
      sx={{
        background: "#273142",
        p: { xs: 2, sm: 3 },
        borderRadius: 3,
        width: "100%",
      }}
    >
      {/* Header */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h6" fontWeight={600} color="white">
          Malaria Trends
        </Typography>

        <Stack direction="row" alignItems="center" gap={2}>
          <Box
            sx={{
              px: 1.6,
              py: 0.6,
              borderRadius: 999,
              background: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.15)",
              color: "white",
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            Last 1 Year
          </Box>

          <Tooltip title="Export as PNG">
            <IconButton sx={{ color: "white" }} onClick={handleExportPNG}>
              <Download size={18} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Chart */}
      <Box ref={chartRef} sx={{ height: { xs: 300, sm: 380, md: 420 } }}>
        <ResponsiveLine
          data={data}
          margin={{ top: 30, right: 50, bottom: 50, left: 50 }}
          xScale={{ type: "point" }}
          yScale={{ type: "linear", min: 0, max: "auto" }}
          curve="monotoneX"
          axisTop={null}
          axisRight={null}
          axisBottom={{ tickPadding: 10, legendOffset: 40 }}
          axisLeft={{ tickPadding: 10, tickValues: 6 }}
          enableGridX={false}
          enableGridY
          colors="#4379EE"
          lineWidth={3}
          pointSize={8}
          pointColor="#ffffff"
          pointBorderWidth={2}
          pointBorderColor="#4379EE"
          enableArea
          areaOpacity={1}
          areaBaselineValue={0}
          useMesh
          /* 🔥 SLICE TOOLTIP REMOVED */
          defs={[
            {
              id: "gradientArea",
              type: "linearGradient",
              colors: [
                { offset: 0, color: "#4379EE29" },
                { offset: 100, color: "rgba(67,121,238,0)" },
              ],
            },
          ]}
          fill={[{ match: "*", id: "gradientArea" }]}
          theme={{
            text: {
              fill: "rgba(255,255,255,0.7)",
              fontSize: isMobile ? 12 : 14,
            },
            axis: {
              ticks: { text: { fill: "rgba(255,255,255,0.6)" } },
            },
            grid: {
              line: { stroke: "rgba(255,255,255,0.08)" },
            },
            tooltip: {
              container: {
                background: "transparent",
                boxShadow: "none",
              },
            },
          }}
          tooltip={({ point }) => (
            <Typography sx={{ color: "white", fontWeight: 600, fontSize: 13 }}>
              {point.data.yFormatted}
            </Typography>
          )}
        />
      </Box>
    </Box>
  );
};

export default LineChart;
